/**
 * The visitor id: the device (BOAT-1895).
 *
 * A UUID we mint in the browser, kept in three places at once and restored from
 * whichever one survives. That is the whole trick — a cookie alone is cleared
 * by a "clear cookies" that leaves localStorage and IndexedDB untouched, and
 * ITP evicts script-written storage that a cookie can outlive. Writing all
 * three and reading them back in order beats casual clearing and a good deal of
 * ITP, which is the highest-value practical step available to us.
 *
 * MINTED CLIENT-SIDE ON PURPOSE. A crawler that does not run JS never gets an
 * id and therefore never creates a row; most of our bot exclusion is that
 * sentence. (The API keeps a user-agent denylist for the ones that do run JS.)
 *
 * `cartId` could not have done this job: it is server-minted, and it is cleared
 * the moment the order is placed — precisely when the device becomes worth
 * remembering.
 *
 * Known limits, by design rather than by surprise: a first-party cookie cannot
 * span ticketed.com and tustickets.es, so the same handset is a different
 * visitor id per brand, and Safari caps first-party storage at seven days of
 * inactivity, so iOS ids churn and device counts overstate. The API keeps
 * `devices` global so the brands can be bridged through the email spine later.
 */

const VISITOR_COOKIE = 'visitorId'
const VISITOR_STORAGE_KEY = 'tc_visitor_id'
const VISITOR_DB_NAME = 'tc-visitor'
const VISITOR_DB_STORE = 'ids'
const VISITOR_DB_KEY = 'visitor_id'

/** 400 days is the ceiling browsers apply to a script-written cookie anyway. */
const VISITOR_COOKIE_MAX_AGE = 60 * 60 * 24 * 400

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const isVisitorId = (value: unknown): value is string =>
  typeof value === 'string' && UUID_PATTERN.test(value.trim())

const mint = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  // Older Safari has crypto.getRandomValues but not randomUUID.
  const bytes = new Uint8Array(16)

  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }

  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'))

  return [
    hex.slice(0, 4).join(''),
    hex.slice(4, 6).join(''),
    hex.slice(6, 8).join(''),
    hex.slice(8, 10).join(''),
    hex.slice(10, 16).join(''),
  ].join('-')
}

/**
 * Every storage call is wrapped: in a private window, with storage blocked, or
 * over a file:// preview, any of them can throw on access rather than return
 * nothing. Tracking must never be the reason a page fails to render.
 */
const readLocalStorage = () => {
  try {
    return window.localStorage.getItem(VISITOR_STORAGE_KEY)
  } catch {
    return null
  }
}

const writeLocalStorage = (visitorId: string) => {
  try {
    window.localStorage.setItem(VISITOR_STORAGE_KEY, visitorId)
  } catch {
    // Nothing to do and nothing to tell the visitor.
  }
}

const openDatabase = () =>
  new Promise<IDBDatabase | null>((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null)

      const request = indexedDB.open(VISITOR_DB_NAME, 1)

      request.onupgradeneeded = () => {
        const db = request.result

        if (!db.objectStoreNames.contains(VISITOR_DB_STORE)) {
          db.createObjectStore(VISITOR_DB_STORE)
        }
      }

      request.onsuccess = () => resolve(request.result)
      request.onerror = () => resolve(null)
      request.onblocked = () => resolve(null)
    } catch {
      resolve(null)
    }
  })

const readIndexedDb = async () => {
  const db = await openDatabase()

  if (!db) return null

  return new Promise<string | null>((resolve) => {
    try {
      const request = db
        .transaction(VISITOR_DB_STORE, 'readonly')
        .objectStore(VISITOR_DB_STORE)
        .get(VISITOR_DB_KEY)

      request.onsuccess = () =>
        resolve(isVisitorId(request.result) ? request.result : null)
      request.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

const writeIndexedDb = async (visitorId: string) => {
  const db = await openDatabase()

  if (!db) return

  try {
    db.transaction(VISITOR_DB_STORE, 'readwrite')
      .objectStore(VISITOR_DB_STORE)
      .put(visitorId, VISITOR_DB_KEY)
  } catch {
    // As above.
  }
}

export const useVisitorId = () => {
  const cookie = useCookie<string | null>(VISITOR_COOKIE, {
    maxAge: VISITOR_COOKIE_MAX_AGE,
    sameSite: 'lax',
    default: () => null,
  })

  // Shared across the app so a page with three signals on it resolves once.
  const visitorId = useState<string | null>('visitorId', () =>
    isVisitorId(cookie.value) ? cookie.value.trim() : null,
  )

  const persist = async (value: string) => {
    visitorId.value = value
    cookie.value = value
    writeLocalStorage(value)
    await writeIndexedDb(value)
  }

  /**
   * The resurrection stack, in confidence order: whatever we already resolved,
   * then the cookie, then localStorage, then IndexedDB — and only then a new
   * id. Whichever one answers is written back to all three, so a store that was
   * cleared is repopulated from the ones that were not.
   */
  const resolveVisitorId = async () => {
    if (import.meta.server) return null

    if (isVisitorId(visitorId.value)) {
      await persist(visitorId.value.trim())

      return visitorId.value
    }

    const stored = readLocalStorage()

    if (isVisitorId(stored)) {
      await persist(stored.trim())

      return visitorId.value
    }

    const resurrected = await readIndexedDb()

    if (isVisitorId(resurrected)) {
      await persist(resurrected.trim())

      return visitorId.value
    }

    await persist(mint())

    return visitorId.value
  }

  return { visitorId, resolveVisitorId }
}
